import React, { FC, Fragment } from 'react';
import styles from './base-content-container.module.css';

// Models
import {
  BaseContentContainerFormat,
  BaseContentContainerBiggerSide,
  BaseContentContainerActionButtonPosition,
} from '../../models/base-content-container';

const BaseContentContainer: FC<{
  format: BaseContentContainerFormat;
  biggerSide?: BaseContentContainerBiggerSide;
  actionButtonPosition?: BaseContentContainerActionButtonPosition;
  withoutShadowBox?: boolean;
  children?: React.ReactNode;
  customContainerStyle?: React.CSSProperties;
}> = ({
  biggerSide,
  format,
  children,
  actionButtonPosition,
  withoutShadowBox,
  customContainerStyle,
}) => {
  const renderSingleWithoutActionButtons = () => {
    return (
      <div
        className={`${styles.singleContainer} ${
          withoutShadowBox ? styles.withoutShadowBox : 'shadow-box'
        }`}
      >
        {children}
      </div>
    );
  };

  const renderDoubleWithoutActionButtons = () => {
    let contentContainerClass: string;

    switch (biggerSide) {
      case 'right':
        contentContainerClass = styles.doubleContainerRightBigger;
        break;
      default:
        contentContainerClass = styles.doubleContainer;
    }

    return (
      <div
        className={`${contentContainerClass} ${
          withoutShadowBox ? styles.withoutShadowBox : 'shadow-box'
        }`}
      >
        <div className={styles.leftContent}>
          {children[0] ? children[0] : null}
        </div>
        <div className={styles.rightContent}>
          {children[1] ? children[1] : null}
        </div>
      </div>
    );
  };

  const renderSingleWithActionButtons = () => {
    const renderContent = (index: number) => {
      if (!children[index]) return null;

      return (
        <div
          className={`${styles.contentContainer} ${
            withoutShadowBox ? styles.withoutShadowBox : 'shadow-box'
          }`}
        >
          {children[index] ? children[index] : null}
        </div>
      );
    };

    if (actionButtonPosition === 'top') {
      return (
        <Fragment>
          <div className={styles.actionContainer}>
            {children[0] ? children[0] : null}
          </div>
          {renderContent(1)}
        </Fragment>
      );
    } else if (actionButtonPosition === 'bottom') {
      return (
        <Fragment>
          {renderContent(0)}
          <div className={styles.actionContainer}>
            {children[1] ? children[1] : null}
          </div>
        </Fragment>
      );
    } else {
      return null;
    }
  };

  const renderDoubleWithActionButtons = () => {
    let contentContainerClass: string;

    switch (biggerSide) {
      case 'right':
        contentContainerClass = styles.contentContainerRightBigger;
        break;
      case 'half':
        contentContainerClass = styles.contentContainerHalf;
        break;
      default:
        contentContainerClass = styles.contentContainer;
    }

    const renderContent = (firstIndex: number, secondIndex: number) => {
      if (!children[firstIndex] || !children[secondIndex]) return null;

      return (
        <div
          className={`${contentContainerClass} ${
            withoutShadowBox ? styles.withoutShadowBox : 'shadow-box'
          }`}
        >
          <div className={styles.leftContent}>
            {children[firstIndex] ? children[firstIndex] : null}
          </div>
          <div className={styles.rightContent}>
            {children[secondIndex] ? children[secondIndex] : null}
          </div>
        </div>
      );
    };

    if (actionButtonPosition === 'top') {
      return (
        <Fragment>
          <div className={styles.actionContainer}>
            {children[0] ? children[0] : null}
          </div>
          {renderContent(1, 2)}
        </Fragment>
      );
    } else if (actionButtonPosition === 'bottom') {
      return (
        <Fragment>
          {renderContent(0, 1)}
          <div className={styles.actionContainer}>
            {children[2] ? children[2] : null}
          </div>
        </Fragment>
      );
    } else {
      return null;
    }
  };

  const renderDoubleUpperAndLargeBottom = () => {
    const title = children[0] ? children[0] : children;
    return (
      <div className={styles.doubleUpperAndLargeBottom}>
        <div className={styles.doubleUpperAndLargeBottomTitle}>{title}</div>
        {children[1] && (
          <div className={styles.doubleUpperAndLargeBottomFirst}>
            <div
              className={[
                styles.doubleUpperAndLargeBottomFirstItem,
                withoutShadowBox ? styles.withoutShadowBox : 'shadow-box',
              ].join(' ')}
            >
              {children[1]}
            </div>

            {children[2] && (
              <div
                className={[
                  styles.doubleUpperAndLargeBottomFirstItem,
                  withoutShadowBox ? styles.withoutShadowBox : 'shadow-box',
                ].join(' ')}
              >
                {children[2]}
              </div>
            )}
          </div>
        )}
        {children[3] && (
          <div
            className={[
              styles.doubleUpperAndLargeBottomSecond,
              withoutShadowBox ? styles.withoutShadowBox : 'shadow-box',
            ].join(' ')}
          >
            {children[3]}
          </div>
        )}
      </div>
    );
  };

  const renderContainer = () => {
    switch (format) {
      case 'singleWithActionButtons':
        return renderSingleWithActionButtons();
      case 'doubleWithActionButtons':
        return renderDoubleWithActionButtons();
      case 'singleWithoutActionButtons':
        return renderSingleWithoutActionButtons();
      case 'doubleWithoutActionButtons':
        return renderDoubleWithoutActionButtons();
      case 'doubleUpperAndLargeBottom':
        return renderDoubleUpperAndLargeBottom();
      default:
        return null;
    }
  };

  if (!children) return null;

  return (
    <div className={styles.baseContainer} style={customContainerStyle}>
      {renderContainer()}
    </div>
  );
};

export default BaseContentContainer;
